// Field modifier system for real-time field updates in ContentEditor
const fieldModifiers = {}

/**
 * Add a field modifier for a specific content type
 * @param {string} contentType - The content type name
 * @param {string} fieldId - The field that triggers the modification
 * @param {function} modifier - Function that takes (fieldValue, allFieldValues) and returns updated field values
 */
export const addFieldModifier = (contentType, fieldId, modifier) => {
  if (!fieldModifiers[contentType]) {
    fieldModifiers[contentType] = {}
  }
  fieldModifiers[contentType][fieldId] = modifier
}

/**
 * Apply field modifiers when a field changes
 * @param {string} contentType - The content type name  
 * @param {string} changedFieldId - The field that was changed
 * @param {*} newValue - The new value of the changed field
 * @param {object} currentValues - All current field values
 * @returns {object} Updated field values with modifications applied
 */
export const applyFieldModifiers = (contentType, changedFieldId, newValue, currentValues) => {
  // Start with the basic field update
  let updatedValues = { ...currentValues, [changedFieldId]: newValue }
  
  // Check if there are modifiers for this content type and field
  const typeModifiers = fieldModifiers[contentType]
  if (!typeModifiers || !typeModifiers[changedFieldId]) return updatedValues
  
  // Apply the modifier
  const modifier = typeModifiers[changedFieldId]
  const modifications = modifier(newValue, updatedValues)
  
  // Merge the modifications
  return { ...updatedValues, ...modifications }
}

/**
 * Check if a field has modifiers
 * @param {string} contentType - The content type name
 * @param {string} fieldId - The field ID to check
 * @returns {boolean}
 */
export const hasFieldModifier = (contentType, fieldId) => {
  return !!(fieldModifiers[contentType] && fieldModifiers[contentType][fieldId])
}

/**
 * Apply all field modifiers for a content type on initial load
 * This runs all modifiers to ensure computed fields are properly set
 * @param {string} contentType - The content type name
 * @param {object} initialValues - The initial field values
 * @returns {object} Updated field values with all modifiers applied
 */
export const applyAllFieldModifiers = (contentType, initialValues) => {
  const typeModifiers = fieldModifiers[contentType]
  if (!typeModifiers) return initialValues
  
  let updatedValues = { ...initialValues }
  
  // Apply each field modifier
  Object.entries(typeModifiers).forEach(([fieldId, modifier]) => {
    const fieldValue = updatedValues[fieldId]
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      const modifications = modifier(fieldValue, updatedValues)
      updatedValues = { ...updatedValues, ...modifications }
    }
  })
  
  return updatedValues
}